import { Link } from 'react-router-dom'

import { Anchor, Button } from '@mantine/core'

import { useState } from 'react'

import { Selector, ChevronDown, ChevronUp } from 'tabler-icons-react'
import Box from '../ui/Box'

// Components
import SKU from '../ui/SKU'
import QuantityDisplay from '../ui/QuantityDisplay'

// User
import useMe from '@/hooks/useMe'

const SortIndicator = ({ orderBy, orderBySlug, orderByDirection }) => {
    if (orderBy === orderBySlug && orderByDirection === 'asc') {
        return <ChevronUp size={15} />
    }

    if (orderBy === orderBySlug && orderByDirection === 'desc') {
        return <ChevronDown size={15} />
    }

    return <Selector size={15} />
}

export default function ComponentChildren({ component, editChildrenHandlers }) {
    const { isAdmin } = useMe()

    const [sortKey, setSortKey] = useState('full_name')
    const [sortDirection, setSortDirection] = useState('asc')

    const sortComparator = (a, b) => {
        if (a[sortKey] < b[sortKey]) {
            return sortDirection === 'asc' ? -1 : 1
        }

        if (a[sortKey] > b[sortKey]) {
            return sortDirection === 'asc' ? 1 : -1
        }

        return 0
    }

    const children = component.children
        .map((cc) => {
            const stockAtLocation1 =
                cc.invlevels.find(
                    (invlevel) =>
                        invlevel.location_id === 1 &&
                        invlevel.invtype === 'physical'
                )?.invlevel ?? null

            const stockAtLocation6 =
                cc.invlevels.find(
                    (invlevel) =>
                        invlevel.location_id === 6 &&
                        invlevel.invtype === 'physical'
                )?.invlevel ?? null

            const full_name = cc.component_category.name_fr + ' — ' + cc.name

            const coverageDays = cc.coverage_days

            const verboseCoverageDays = coverageDays
                ? Math.floor(coverageDays / 30) + ' mois'
                : '-'

            return {
                quantity: cc.pivot.quantity,
                cc,
                full_name,
                stockAtLocation1,
                stockAtLocation6,
                coverageDays,
                verboseCoverageDays,
            }
        })
        .sort(sortComparator)

    const toggleSort = (key) => {
        if (key === sortKey) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
        } else {
            setSortKey(key)
            setSortDirection('asc')
        }
    }

    const rightSection = (
        <Button variant="default" onClick={editChildrenHandlers.open}>
            Modifier
        </Button>
    )

    return (
        <Box
            title="Éléments"
            subtitle="Composants utilisés dans les assemblages"
            rightSection={rightSection}
        >
            <div className="flex flex-col mt-5 border">
                <div className="flex w-full p-1.5 border-b">
                    <button
                        className="flex items-center gap-2 flex-1"
                        onClick={() => {
                            toggleSort('full_name')
                        }}
                    >
                        <span>Éléments</span>

                        <SortIndicator
                            orderBy={sortKey}
                            orderBySlug="full_name"
                            orderByDirection={sortDirection}
                        />
                    </button>

                    <button
                        className="flex justify-end items-center gap-2 w-[120px]"
                        onClick={() => {
                            toggleSort('coverageDays')
                        }}
                    >
                        <span>Couverture</span>

                        <SortIndicator
                            orderBy={sortKey}
                            orderBySlug="coverageDays"
                            orderByDirection={sortDirection}
                        />
                    </button>

                    <button
                        className="flex items-center justify-end gap-2 w-[120px]"
                        onClick={() => {
                            toggleSort('stockAtLocation1')
                        }}
                    >
                        <span>MNP</span>

                        <SortIndicator
                            orderBy={sortKey}
                            orderBySlug="stockAtLocation1"
                            orderByDirection={sortDirection}
                        />
                    </button>

                    {isAdmin && (
                        <button
                            className="flex items-center justify-end gap-2 w-[120px]"
                            onClick={() => {
                                toggleSort('stockAtLocation6')
                            }}
                        >
                            <span>Atelier</span>

                            <SortIndicator
                                orderBy={sortKey}
                                orderBySlug="stockAtLocation6"
                                orderByDirection={sortDirection}
                            />
                        </button>
                    )}
                </div>

                {children.map((child) => {
                    const cc = child.cc

                    return (
                        <div
                            className="flex items-center w-full odd:bg-gray-100 p-1.5"
                            key={child}
                        >
                            <div className="flex items-center gap-2 truncate flex-1">
                                <span className="text-gray-600 font-mono">
                                    ×{child.quantity}
                                </span>

                                <SKU component={cc} size="sm" />

                                <Link
                                    className="group truncate"
                                    to={'/components/' + cc.id}
                                >
                                    <Anchor component="span">
                                        {cc.component_category.name_fr} —{' '}
                                        {cc.name}
                                    </Anchor>
                                </Link>
                            </div>

                            <div className="flex flex-col items-end text-sm shrink-0 w-[120px]">
                                <span className="text-gray-500">
                                    {child.verboseCoverageDays}
                                </span>
                            </div>

                            <div className="flex flex-col items-end text-sm shrink-0 w-[120px]">
                                <QuantityDisplay
                                    quantity={child.stockAtLocation1}
                                    greenWhenPositive={true}
                                    grayLabel={
                                        child.stockAtLocation1 === null &&
                                        'Non stocké'
                                    }
                                />
                            </div>

                            {isAdmin && (
                                <div className="flex flex-col items-end text-sm shrink-0 w-[120px]">
                                    <QuantityDisplay
                                        quantity={child.stockAtLocation6}
                                        greenWhenPositive={true}
                                        grayLabel={
                                            child.stockAtLocation6 === null &&
                                            'Non stocké'
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        </Box>
    )
}
